import React, { useEffect, useId, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import {
  Button, ConfirmDialog, Field, PanelBody, PanelHeader, SidePanel, Switch,
} from '../kit';
import { apiErrorMessage, createOrganization, getCurrencies } from '../../../utils/organizationApi';
import { SUPPORTED_CURRENCIES } from '../../../utils/currency';
import { currencyOptions, MFA_METHODS, mfaHelp, validateGeneral } from './orgModel';
import { CurrencySelect } from './orgsUi';

const blank = () => ({
  name: '',
  code: '',
  reportingCurrency: 'USD',
  financialCloseDay: 15,
  mfaEnforced: false,
  mfaMethods: ['TOTP', 'EMAIL'],
  journalRestricted: false,
});

/**
 * Create an organization: POST /organizations provisions the SQL row, the
 * Cosmos org-config doc (seeded with the MFA policy + journal gate below) and
 * gives the creator orgAdmin. The caller navigates to the new org.
 */
export default function AddOrganizationPanel({ open, onClose, onCreated }) {
  const [form, setForm] = useState(blank);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [currencies, setCurrencies] = useState([]);
  const ids = { name: useId(), code: useId(), currency: useId(), close: useId() };

  useEffect(() => {
    if (!open) return;
    setForm(blank());
    setErrors({});
    getCurrencies().then(setCurrencies).catch(() => setCurrencies([]));
  }, [open]);

  const options = useMemo(() => currencyOptions(currencies, SUPPORTED_CURRENCIES), [currencies]);
  const dirty = Boolean(form.name.trim() || form.code.trim());

  const set = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const requestClose = () => {
    if (saving) return;
    if (dirty) setConfirmDiscard(true);
    else onClose();
  };

  const submit = async () => {
    const next = validateGeneral(form);
    if (form.mfaEnforced && !form.mfaMethods.length) next.mfaMethods = 'Pick at least one sign-in method.';
    setErrors(next);
    if (Object.keys(next).length) return;
    setSaving(true);
    try {
      const created = await createOrganization({
        name: form.name.trim(),
        code: form.code.trim(),
        reportingCurrency: form.reportingCurrency,
        financialCloseDay: Number(form.financialCloseDay),
        // seeded into the new org-config doc by the API
        mfaPolicy: { enforced: form.mfaEnforced, allowedMethods: form.mfaEnforced ? form.mfaMethods : [] },
        journalAdjustmentCategories: form.journalRestricted ? [] : null,
      });
      const failed = created?.provisioning?.errors || [];
      if (failed.length) toast.error(`Organization created, but setup was incomplete — ${failed.join('; ')}`, { duration: 9000 });
      else toast.success(`${created?.name || 'Organization'} created`);
      onCreated(created);
    } catch (err) {
      const status = err?.response?.status;
      const message = apiErrorMessage(err, 'Could not create the organization.');
      if (status === 409) setErrors((prev) => ({ ...prev, code: message }));
      else toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const toggleMethod = (key) => set('mfaMethods', form.mfaMethods.includes(key)
    ? form.mfaMethods.filter((m) => m !== key)
    : [...form.mfaMethods, key]);

  return (
    <>
      <SidePanel open={open} onRequestClose={requestClose} label="Add organization" narrow>
        <PanelHeader
          title="Add organization"
          sub="Creates the organization and its org config, and gives you admin access to it."
          onClose={requestClose}
          bordered
        />
        <PanelBody>
          <div className="orgs-section">
            <span className="ak-section-label">General</span>
            <div className="ak-grid ak-2">
              <Field label="Name" required error={errors.name} htmlFor={ids.name}>
                <input id={ids.name} className="ak-input" value={form.name} autoFocus aria-invalid={Boolean(errors.name)} onChange={(e) => set('name', e.target.value)} />
              </Field>
              <Field label="Code" required error={errors.code} help="Short and unique, e.g. HLH. Access is keyed by it." htmlFor={ids.code}>
                <input id={ids.code} className="ak-input" value={form.code} aria-invalid={Boolean(errors.code)} onChange={(e) => set('code', e.target.value.toUpperCase())} />
              </Field>
              <Field label="Portfolio currency" error={errors.reportingCurrency} htmlFor={ids.currency}>
                <CurrencySelect id={ids.currency} value={form.reportingCurrency} options={options} onChange={(v) => set('reportingCurrency', v)} />
              </Field>
              <Field label="Financial close day" error={errors.financialCloseDay} help="Reports treat a month as closed after this day." htmlFor={ids.close}>
                <input id={ids.close} className="ak-input" type="number" min="1" max="31" value={form.financialCloseDay} aria-invalid={Boolean(errors.financialCloseDay)} onChange={(e) => set('financialCloseDay', e.target.value)} />
              </Field>
            </div>
          </div>

          <div className="orgs-section">
            <span className="ak-section-label">Policies</span>
            <div className="orgs-policy">
              <Switch checked={form.mfaEnforced} onChange={(v) => set('mfaEnforced', v)} title="Require MFA to sign in" />
              <div className="orgs-policy-body">
                <span className="orgs-policy-title">Require MFA to sign in</span>
                <span className="ak-help">{form.mfaEnforced ? mfaHelp(form.mfaMethods) : 'Off: users sign in with a password only.'}</span>
                {form.mfaEnforced ? (
                  <div className="orgs-checks" role="group" aria-label="Allowed sign-in methods">
                    {MFA_METHODS.map((m) => (
                      <label key={m.key} className="orgs-check">
                        <input type="checkbox" className="ak-check" checked={form.mfaMethods.includes(m.key)} onChange={() => toggleMethod(m.key)} />
                        <span>{m.label}<small>{m.help}</small></span>
                      </label>
                    ))}
                  </div>
                ) : null}
                {errors.mfaMethods ? <span className="ak-field-error" role="alert">{errors.mfaMethods}</span> : null}
              </div>
            </div>
            <div className="orgs-policy">
              <Switch checked={form.journalRestricted} onChange={(v) => set('journalRestricted', v)} title="Limit journal adjustments by category" />
              <div className="orgs-policy-body">
                <span className="orgs-policy-title">Limit journal adjustments by category</span>
                <span className="ak-help">
                  {form.journalRestricted
                    ? 'Adjustments start switched off. Allow categories on the Settings tab once journal types are loaded.'
                    : 'Off: adjustments are allowed for every journal category.'}
                </span>
              </div>
            </div>
          </div>
        </PanelBody>
        <footer className="ak-panel-foot">
          <span className="ak-muted" style={{ fontSize: 13 }}>You can add locations next.</span>
          <span className="ak-foot-spacer" />
          <Button onClick={requestClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={submit} disabled={saving}>{saving ? 'Creating…' : 'Create organization'}</Button>
        </footer>
      </SidePanel>
      <ConfirmDialog
        open={confirmDiscard}
        title="Discard this organization?"
        confirmLabel="Discard"
        danger
        onCancel={() => setConfirmDiscard(false)}
        onConfirm={() => { setConfirmDiscard(false); onClose(); }}
      >
        <p style={{ margin: 0 }}>Nothing has been created yet. What you entered will be lost.</p>
      </ConfirmDialog>
    </>
  );
}
